import DateTimePicker from "@/components/DateTimePicker";
import Field from "@/components/Field";
import Form from "@/components/Form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isRequired } from "@/lib/form";
import { useConfirmContractorVisitsMutation } from "@/modules/projects/components/contractors-visits/hooks/useConfirmContractorVisitsMutation";
import {
  confirmContractorVisit,
  confirmContractorVisitBase,
} from "@/schemas/contractorVisit";
import {
  ConfirmContractorVisitForm,
  ReadContractorVisit,
} from "@/types/contractorVisit";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useContractorVisitDateTimeDefaultValues } from "../hooks/useContractorVisitDateTimeDefaultValues";
import { ContractorVisitDate } from "./ContractorVisitDate";

interface ContractorVisitConfirmDialogProps {
  visit?: ReadContractorVisit;
  isOpen: boolean;
  onClose: () => void;
}

export const ContractorVisitConfirmDialog = memo(
  ({ visit, isOpen, onClose }: ContractorVisitConfirmDialogProps) => {
    const { t } = useTranslation();
    const confirmMutation = useConfirmContractorVisitsMutation(visit?.id || "");
    const defaultValues = useContractorVisitDateTimeDefaultValues(visit);

    const form = useForm<ConfirmContractorVisitForm>({
      resolver: zodResolver(confirmContractorVisit),
      defaultValues,
    });

    useEffect(() => {
      if (isOpen) {
        form.reset(defaultValues);
      }
    }, [isOpen, defaultValues, form]);

    const handleSubmit = useCallback(
      async (data: ConfirmContractorVisitForm) => {
        await confirmMutation.mutateAsync(data);
        onClose();
      },
      [confirmMutation, onClose],
    );

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t("projectContractorVisits.confirmDialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("projectContractorVisits.confirmDialog.description")}
              <ContractorVisitDate date={visit?.date} />
            </DialogDescription>
          </DialogHeader>

          <Form form={form} handleSubmit={handleSubmit} className="space-y-4">
            <Field
              name="start_datetime"
              label={t("projectContractorVisits.fields.start_datetime")}
              required={isRequired(
                confirmContractorVisitBase.shape.start_datetime,
              )}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  disabled={confirmMutation.isPending}
                  className="w-full"
                />
              )}
            />

            <Field
              name="end_datetime"
              label={t("projectContractorVisits.fields.end_datetime")}
              required={isRequired(
                confirmContractorVisitBase.shape.end_datetime,
              )}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  disabled={confirmMutation.isPending}
                  className="w-full"
                />
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={confirmMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={confirmMutation.isPending}>
                {t("projectContractorVisits.actions.confirm")}
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    );
  },
);
